import { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Currency Context — handles USD / MXN display.
 * - Persists user's preferred currency in localStorage.
 * - Fetches an approximate USD → MXN rate once per day (free public API).
 * - Falls back to a sensible static rate if the network call fails.
 *
 * IMPORTANT: All product prices and budgets are stored on the backend in the
 * provider-chosen currency (`amount_currency` field).
 * The conversion here is *display only* — Stripe always charges in the
 * actual currency stored on the booking, never in the toggle currency.
 */

const STATIC_FALLBACK_USD_TO_MXN = 18.5;
const FX_CACHE_KEY = 'bidtrip_fx_usd_mxn';
const FX_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};

const loadCachedRate = () => {
  try {
    const raw = localStorage.getItem(FX_CACHE_KEY);
    if (!raw) return null;
    const { rate, ts } = JSON.parse(raw);
    if (!rate || !ts) return null;
    if (Date.now() - ts > FX_CACHE_TTL_MS) return null;
    return rate;
  } catch {
    return null;
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('currency');
    if (saved === 'USD' || saved === 'MXN') return saved;
    // Default: MXN if browser language starts with es, else USD.
    const lang = (navigator.language || '').toLowerCase();
    return lang.startsWith('es') ? 'MXN' : 'USD';
  });

  const [usdToMxn, setUsdToMxn] = useState(() => loadCachedRate() || STATIC_FALLBACK_USD_TO_MXN);

  // Persist user choice
  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  // Refresh FX rate (best-effort, once on mount + when cache expires)
  useEffect(() => {
    if (loadCachedRate()) return;
    let cancelled = false;
    (async () => {
      try {
        // Free, no-key API
        const res = await fetch('https://open.er-api.com/v6/latest/USD');
        if (!res.ok) return;
        const json = await res.json();
        const rate = json?.rates?.MXN;
        if (cancelled || !rate || typeof rate !== 'number') return;
        setUsdToMxn(rate);
        localStorage.setItem(FX_CACHE_KEY, JSON.stringify({ rate, ts: Date.now() }));
      } catch {
        /* keep fallback */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleCurrency = useCallback(() => {
    setCurrency((c) => (c === 'USD' ? 'MXN' : 'USD'));
  }, []);

  /**
   * Convert an amount from `fromCurrency` to the user's selected display currency.
   * Returns a Number, NOT a string.
   */
  const convert = useCallback(
    (amount, fromCurrency = 'USD', toCurrency = currency) => {
      if (amount == null || isNaN(amount)) return 0;
      const num = Number(amount);
      if (fromCurrency === toCurrency) return num;
      if (fromCurrency === 'USD' && toCurrency === 'MXN') return num * usdToMxn;
      if (fromCurrency === 'MXN' && toCurrency === 'USD') return num / usdToMxn;
      return num;
    },
    [currency, usdToMxn]
  );

  /**
   * Format an amount for display.
   *  - If `displayInToggle` is true, converts to the user's toggle currency first.
   *  - Otherwise renders the amount in its native currency.
   */
  const format = useCallback(
    (amount, sourceCurrency = 'USD', opts = {}) => {
      const { displayInToggle = true, decimals = 0, withCode = true } = opts;
      const target = displayInToggle ? currency : sourceCurrency;
      const value = displayInToggle ? convert(amount, sourceCurrency, target) : Number(amount || 0);
      const formatted = value.toLocaleString(target === 'MXN' ? 'es-MX' : 'en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      const symbol = target === 'MXN' ? '$' : '$';
      const code = withCode ? ` ${target}` : '';
      return `${symbol}${formatted}${code}`;
    },
    [currency, convert]
  );

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        toggleCurrency,
        usdToMxn,
        convert,
        format,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
